import React, { useState, useRef, useEffect } from 'react';

export default function DraggablePlayerSlot({
  playerName,
  isWinner,
  isLoser, // Track if this player lost the match
  isFinished,
  onSwapPlayers,
  matchId,
  playerSlot, // 'p1' or 'p2'
  canRearrange,
  style = {},
  onViewProfile = null, // Optional callback for viewing player profile
  playerUserId = null, // Optional user ID for profile lookup
  isDoubles = false // If true, abbreviate last names; if false (singles), show full names
}) {
  const [isDragOver, setIsDragOver] = useState(false);
  const [isTouchDragging, setIsTouchDragging] = useState(false);
  const scrollIntervalRef = useRef(null);
  const currentScrollDirection = useRef(null); // Track current scroll direction
  const touchHoldTimerRef = useRef(null);
  const touchStartPosRef = useRef({ x: 0, y: 0 });
  const isDraggingRef = useRef(false);
  const draggedPlayerRef = useRef(null);
  
  // Auto-scroll functionality with smoother behavior
  const startAutoScroll = (clientY) => {
    const scrollZone = 150; // pixels from edge to start scrolling
    const scrollSpeed = 8; // reduced speed for smoother scrolling
    const scrollInterval = 20; // slightly slower interval for stability
    
    const viewportHeight = window.innerHeight;
    const scrollElement = document.scrollingElement || document.documentElement;
    
    let newDirection = null;
    if (clientY < scrollZone) {
      newDirection = 'up';
    } else if (clientY > viewportHeight - scrollZone) {
      newDirection = 'down';
    } else {
      newDirection = null;
    }
    
    // Only restart interval if direction changed or no interval is running
    if (currentScrollDirection.current !== newDirection) {
      // Stop current scrolling
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current);
        scrollIntervalRef.current = null;
      }
      
      currentScrollDirection.current = newDirection;
      
      // Start new scrolling if needed
      if (newDirection) {
        scrollIntervalRef.current = setInterval(() => {
          if (newDirection === 'up') {
            // Scroll up
            const newScrollTop = Math.max(0, scrollElement.scrollTop - scrollSpeed);
            if (scrollElement.scrollTop !== newScrollTop) {
              scrollElement.scrollTop = newScrollTop;
            }
          } else if (newDirection === 'down') {
            // Scroll down
            const maxScroll = scrollElement.scrollHeight - scrollElement.clientHeight;
            const newScrollTop = Math.min(maxScroll, scrollElement.scrollTop + scrollSpeed);
            if (scrollElement.scrollTop !== newScrollTop) {
              scrollElement.scrollTop = newScrollTop;
            }
          }
        }, scrollInterval);
      }
    }
  };
  
  const stopAutoScroll = () => {
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }
    currentScrollDirection.current = null; // Reset direction tracking
  };

  // Touch event handlers for mobile drag-and-drop
  const handleTouchStart = (e) => {
    if (!canRearrange) return;

    const touch = e.touches[0];
    touchStartPosRef.current = { x: touch.clientX, y: touch.clientY };

    // Start a timer for tap-and-hold
    touchHoldTimerRef.current = setTimeout(() => {
      // Vibrate if supported (haptic feedback)
      if (navigator.vibrate) {
        navigator.vibrate(50);
      }

      isDraggingRef.current = true;
      draggedPlayerRef.current = playerName;
      setIsTouchDragging(true);

      // Start auto-scroll tracking
      startAutoScroll(touch.clientY);
    }, 500); // 500ms hold to start dragging
  };

  const handleTouchMove = (e) => {
    if (!canRearrange) return;

    const touch = e.touches[0];
    const moveDistance = Math.sqrt(
      Math.pow(touch.clientX - touchStartPosRef.current.x, 2) +
      Math.pow(touch.clientY - touchStartPosRef.current.y, 2)
    );

    // If user moves finger before hold timer completes, cancel the hold
    if (moveDistance > 10 && !isDraggingRef.current) {
      if (touchHoldTimerRef.current) {
        clearTimeout(touchHoldTimerRef.current);
        touchHoldTimerRef.current = null;
      }
      return;
    }

    // If currently dragging, update auto-scroll
    if (isDraggingRef.current) {
      e.preventDefault(); // Prevent scrolling while dragging
      startAutoScroll(touch.clientY);

      // Check if hovering over another player slot
      const elementAtPoint = document.elementFromPoint(touch.clientX, touch.clientY);
      if (elementAtPoint) {
        const playerSlot = elementAtPoint.closest('[data-player-slot]');
        if (playerSlot && playerSlot.dataset.playerName !== draggedPlayerRef.current) {
          // Highlight the target using theme-aware colors
          playerSlot.style.borderColor = 'var(--info)';
          playerSlot.style.borderWidth = '2px';
        }
      }
    }
  };

  const handleTouchEnd = (e) => {
    if (!canRearrange) return;

    // Clear hold timer if it hasn't triggered yet
    if (touchHoldTimerRef.current) {
      clearTimeout(touchHoldTimerRef.current);
      touchHoldTimerRef.current = null;
    }

    stopAutoScroll();

    // If we were dragging, check for drop target
    if (isDraggingRef.current) {
      const touch = e.changedTouches[0];
      const elementAtPoint = document.elementFromPoint(touch.clientX, touch.clientY);

      if (elementAtPoint) {
        const playerSlot = elementAtPoint.closest('[data-player-slot]');
        if (playerSlot) {
          const targetPlayerName = playerSlot.dataset.playerName;
          if (targetPlayerName && targetPlayerName !== draggedPlayerRef.current) {
            onSwapPlayers(draggedPlayerRef.current, targetPlayerName);
          }

          // Reset target styling
          playerSlot.style.borderColor = '';
          playerSlot.style.borderWidth = '';
        }
      }

      // Vibrate to confirm drop
      if (navigator.vibrate) {
        navigator.vibrate(30);
      }
    }

    // Reset dragging state
    isDraggingRef.current = false;
    draggedPlayerRef.current = null;
    setIsTouchDragging(false);
  };

  const handleTouchCancel = () => {
    if (touchHoldTimerRef.current) {
      clearTimeout(touchHoldTimerRef.current);
      touchHoldTimerRef.current = null;
    }
    stopAutoScroll();
    isDraggingRef.current = false;
    draggedPlayerRef.current = null;
    setIsTouchDragging(false);
  };

  const handleDragStart = (e) => {
    if (!canRearrange) {
      e.preventDefault();
      return;
    }
    
    e.dataTransfer.setData('text/plain', JSON.stringify({
      playerName,
      matchId,
      playerSlot
    }));
    e.dataTransfer.effectAllowed = 'move';
    
    // Start tracking mouse position for auto-scroll
    const handleGlobalDragOver = (dragEvent) => {
      startAutoScroll(dragEvent.clientY);
    };
    
    // Add global drag event listener
    document.addEventListener('dragover', handleGlobalDragOver);
    
    // Clean up on drag end
    const handleGlobalDragEnd = () => {
      document.removeEventListener('dragover', handleGlobalDragOver);
      document.removeEventListener('dragend', handleGlobalDragEnd);
      stopAutoScroll();
    };
    
    document.addEventListener('dragend', handleGlobalDragEnd);
  };
  
  const handleDragOver = (e) => {
    if (!canRearrange) return;
    
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setIsDragOver(true);
  };
  
  const handleDragLeave = () => {
    setIsDragOver(false);
  };
  
  const handleDrop = (e) => {
    if (!canRearrange) return;
    
    e.preventDefault();
    setIsDragOver(false);
    
    try {
      const dragData = JSON.parse(e.dataTransfer.getData('text/plain'));
      const draggedPlayerName = dragData.playerName;
      
      if (draggedPlayerName !== playerName) {
        onSwapPlayers(draggedPlayerName, playerName);
      }
    } catch (error) {
      console.error('Error parsing drag data:', error);
    }
  };
  
  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      stopAutoScroll();
      if (touchHoldTimerRef.current) {
        clearTimeout(touchHoldTimerRef.current);
      }
    };
  }, []);
  
  // Separate style props to avoid background/backgroundColor conflicts
  const { background, backgroundColor, ...safeStyle } = style || {};

  const baseStyle = {
    padding: '2px 4px',
    border: '1px solid var(--border-color)',
    borderRadius: 3,
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    cursor: canRearrange ? 'grab' : 'default',
    transition: 'all 0.2s ease',
    userSelect: 'none',
    touchAction: 'none', // Prevent default touch actions
    textAlign: 'center',
    ...safeStyle
  };

  // Determine base background color (also check for touch dragging)
  // Use CSS variables for theme compatibility
  // Winner no longer gets special background - trophy icon + bold text indicates winner instead
  const baseBackgroundColor = (isDragOver || isTouchDragging) ? 'var(--info-bg)' : (isFinished ? 'var(--bg-tertiary)' : 'var(--bg-secondary)');

  const dragStyle = {
    ...baseStyle,
    borderColor: (isDragOver || isTouchDragging) ? 'var(--info)' : 'var(--border-color)',
    borderWidth: (isDragOver || isTouchDragging) ? '2px' : '1px',
    boxShadow: (isDragOver || isTouchDragging) ? 'var(--shadow-md)' : 'none',
    transform: isTouchDragging ? 'scale(1.05)' : 'scale(1)', // Slightly enlarge when dragging
    opacity: isTouchDragging ? 0.8 : (isLoser ? 0.55 : 1), // Dim loser's name for better winner visibility
    cursor: canRearrange ? 'grab' : 'default' // Ensure cursor is applied after all other styles
  };

  // Set background (using only background property to avoid conflicts)
  if (canRearrange) {
    if (playerName === "BYE") {
      // Special styling for BYE rounds to distinguish them (using warning color for visibility)
      dragStyle.background = `linear-gradient(45deg, ${baseBackgroundColor} 0%, ${baseBackgroundColor} 90%, var(--warning-bg) 90%, var(--warning-bg) 100%)`;
      dragStyle.fontStyle = 'italic';
      dragStyle.opacity = 0.8;
    } else {
      dragStyle.background = `linear-gradient(45deg, ${baseBackgroundColor} 0%, ${baseBackgroundColor} 90%, var(--bg-tertiary) 90%, var(--bg-tertiary) 100%)`;
    }
  } else {
    // Use solid background for non-draggable items
    dragStyle.background = baseBackgroundColor;
  }
  
  // Handle profile click
  const handleProfileClick = (e) => {
    if (onViewProfile && playerUserId) {
      e.stopPropagation();
      e.preventDefault();
      onViewProfile(playerUserId, playerName);
    }
  };

  // Determine if this player name should be clickable for profile viewing
  const isProfileClickable = onViewProfile && playerUserId && !canRearrange;

  // Format name based on tournament type:
  // - Singles: Show full name (e.g., "Ian Waters")
  // - Doubles: Abbreviate last name (e.g., "Ian Waters & John Smith" -> "Ian W. & John S.")
  const formatDisplayName = (fullName) => {
    if (!fullName || fullName === "BYE") return fullName;

    // For singles tournaments, show the full name
    if (!isDoubles) {
      return fullName;
    }

    // For doubles, abbreviate last names to save space
    // Handle doubles format with "&"
    if (fullName.includes(' & ')) {
      const players = fullName.split(' & ');
      return players.map(p => formatSingleNameAbbreviated(p)).join(' & ');
    }

    return formatSingleNameAbbreviated(fullName);
  };

  // Format a single player name with abbreviation: "First Last" -> "First L."
  const formatSingleNameAbbreviated = (name) => {
    if (!name) return name;
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0]; // Single name, no change

    // First name + last initial with period
    const firstName = parts[0];
    const lastInitial = parts[parts.length - 1].charAt(0).toUpperCase() + '.';
    return `${firstName} ${lastInitial}`;
  };

  const displayName = formatDisplayName(playerName);

  // Build tooltip text - always show full name, plus context-specific instructions
  const getTooltipText = () => {
    if (canRearrange) {
      if (playerName === "BYE") {
        return `Tap and hold to move this BYE to another position`;
      }
      return `${playerName}\n(Tap and hold to swap with another player)`;
    }
    if (isProfileClickable) {
      return `${playerName}\n(Click to view profile)`;
    }
    return playerName;
  };

  return (
    <span
      style={{
        ...dragStyle,
        cursor: isProfileClickable ? 'pointer' : dragStyle.cursor,
        textDecoration: isProfileClickable ? 'underline' : 'none',
        color: isProfileClickable ? 'var(--btn-primary)' : undefined
      }}
      draggable={canRearrange}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onTouchStart={canRearrange ? handleTouchStart : undefined}
      onTouchMove={canRearrange ? handleTouchMove : undefined}
      onTouchEnd={canRearrange ? handleTouchEnd : undefined}
      onTouchCancel={canRearrange ? handleTouchCancel : undefined}
      onClick={isProfileClickable ? handleProfileClick : undefined}
      data-player-slot="true"
      data-player-name={playerName}
      title={getTooltipText()}
    >
      {canRearrange && <span style={{ opacity: 0.5, fontSize: '10px', marginRight: '4px' }}>⋮⋮</span>}
      {isWinner && <span style={{ marginRight: '3px', fontSize: '10px' }}>🏆</span>}
      <span style={{ fontWeight: isWinner ? 700 : 'normal' }}>{displayName}</span>
      {isTouchDragging && <span style={{ marginLeft: '4px', fontSize: '12px' }}>↕</span>}
    </span>
  );
}